package dev.terrakok.mapitan.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import dev.terrakok.mapitan.domain.LineStyle

@Composable
fun LineStylePicker(
    title: String,
    selectedStyle: LineStyle,
    onStyleSelected: (LineStyle) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Medium,
        )

        Row(
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            LineStyle.entries.forEach { style ->
                LineStyleSwatch(
                    style = style,
                    isSelected = style == selectedStyle,
                    onClick = { onStyleSelected(style) },
                )
            }
        }
    }
}

@Composable
private fun LineStyleSwatch(
    style: LineStyle,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .size(32.dp)
            .then(
                if (isSelected) {
                    Modifier.border(
                        width = 2.dp,
                        color = MaterialTheme.colorScheme.primary,
                        shape = RoundedCornerShape(8.dp),
                    )
                } else {
                    Modifier
                },
            )
            .clip(RoundedCornerShape(6.dp))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(4.dp)
                .clip(RoundedCornerShape(6.dp))
                .background(MaterialTheme.colorScheme.surfaceContainer),
        )
        val color = MaterialTheme.colorScheme.primary
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(3.dp)
                .padding(horizontal = 4.dp)
                .drawBehind {
                    val padding = 4.dp.toPx()
                    when (style) {
                        LineStyle.Solid -> {
                            drawLine(
                                color = color,
                                start = Offset(padding, size.height / 2f),
                                end = Offset(size.width - padding, size.height / 2f),
                                strokeWidth = size.height,
                                cap = StrokeCap.Round
                            )
                        }

                        LineStyle.Dashed -> {
                            val dashLen = (size.width - 2 * padding) / 3f
                            val midY = size.height / 2f
                            drawLine(
                                color = color,
                                start = Offset(padding, midY),
                                end = Offset(padding + dashLen, midY),
                                strokeWidth = size.height,
                                cap = StrokeCap.Round
                            )
                            drawLine(
                                color = color,
                                start = Offset(padding + 2 * dashLen, midY),
                                end = Offset(size.width - padding, midY),
                                strokeWidth = size.height,
                                cap = StrokeCap.Round
                            )
                        }

                        LineStyle.Dotted -> {
                            val dotRadius = size.height / 2f
                            val gap = (size.width - 2 * (padding + dotRadius)) / 2f
                            val midY = size.height / 2f
                            var x = padding + dotRadius
                            repeat(3) {
                                drawOval(
                                    color = color,
                                    topLeft = Offset(x - dotRadius, midY - dotRadius),
                                    size = Size(dotRadius * 2, dotRadius * 2),
                                )
                                x += gap
                            }
                        }
                    }
                }
                .clip(RoundedCornerShape(6.dp)),
        )
    }
}
